// Block-2-Karte „Geburtstage" (AP-G, DD1/DD9): zeigt die Kunden/Kontakte, die
// HEUTE Geburtstag haben (Tag/Monat = heute) — nur Kontakte, keine Mitarbeiter.
// Quelle: `dashboardService.getBirthdaysToday()` → `GET /dashboard/birthdays/today`
// (serverseitiger Tag/Monat-Filter über `Contact.birthDate`, keine Migration).
// Jede Zeile (Avatar + Name + ggf. Alter) verlinkt auf die Kontakt-Detailseite.
//
// Dashboard-Termine-Review SP2 (2026-07-05): ZUSÄTZLICH die heutigen
// Geburtstags-Termine aus dem eigenen Outlook-Kalender (Graph) anzeigen — als
// eigene, mit „Outlook"-Badge gekennzeichnete Zeilen UNTER den Kontakten. Diese
// Zeilen sind rein informativ (Entscheidung 5): kein Alter, kein Kontakt-Link,
// nicht klickbar. Ein Kontakt-Geburtstag mit gleichem (normalisierten) Namen
// unterdrückt den Outlook-Eintrag (Kontakt gewinnt — hat Link, Alter, Status).
// Bewusst KEIN Backend-Umbau: dieselbe Graph-Quelle wie die Termine-Karte.
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Gift20Regular } from "@fluentui/react-icons";
import { calendarService } from "../../services/calendarService";
import { dashboardService, type DashboardBirthday } from "../../services/dashboardService";
import { istGeburtstagEvent } from "../calendar/eventHeuristics";
import { useAuth } from "../../context/AuthContext";
import { REFINED } from "../risikoanalyseStep/tokens";
import {
    DashCard,
    EmptyHint,
    InitialsAvatar,
    isToday,
    MehrLadenButton,
    OutlookBadge,
    startOfDay,
    StatusChip,
} from "./DashboardShared";

/** Montag (00:00) der aktuellen Woche — identisch zur Termine-Karte, damit sich
 *  beide denselben Kalender-Cache-Eintrag teilen (L1). */
const mondayOfWeek = (ref: Date): Date => {
    const d = startOfDay(ref);
    const wd = (d.getDay() + 6) % 7; // Mo=0 … So=6
    d.setDate(d.getDate() - wd);
    return d;
};

/** P5: max. Anzahl direkt sichtbarer Geburtstage (Rest hinter „Mehr" + Scroll). */
const VISIBLE = 10;

/** Ein Outlook-Geburtstag (rein informativ, SP2) — nur Id + aufbereiteter Name. */
interface OutlookBirthday {
    id: string;
    name: string;
}

/** Gemischter Karten-Eintrag: KMVS-Kontakt (klickbar) oder Outlook (informativ). */
type BirthdayRowItem =
    | { kind: "contact"; contact: DashboardBirthday }
    | { kind: "outlook"; outlook: OutlookBirthday };

/**
 * Betreff eines Outlook-Geburtstags-Events auf den reinen Namen reduzieren.
 * Formate variieren stark („Geburtstag von X", „X's Birthday", nur Name + 🎂).
 * Robust strippen — bleibt nichts übrig, den rohen (getrimmten) Betreff zeigen,
 * NIE einen leeren Namen rendern.
 */
export const parseOutlookBirthdayName = (subject: string): string => {
    const raw = (subject || "").trim();
    let name = raw
        // Party-/Geburtstags-Emojis entfernen (führend, mittig, anschließend).
        .replace(/[\u{1F382}\u{1F389}\u{1F388}\u{1F973}]/gu, "")
        .trim();
    // Deutsche Präfixe: „Geburtstag von X", „Geburtstag: X", „Geburtstag X".
    name = name.replace(/^geburtstag(?:\s+von\b|:)?\s*/i, "");
    // Englische Präfixe: „Birthday of X", „Birthday: X".
    name = name.replace(/^birthday(?:\s+of\b|:)?\s*/i, "");
    // Englische Possessiv-Suffixe: „X's Birthday", „X’s Geburtstag".
    name = name.replace(/['’]s\s+(?:birthday|geburtstag)\s*$/i, "");
    // Mehrfach-Leerzeichen glätten.
    name = name.replace(/\s{2,}/g, " ").trim();
    return name.length > 0 ? name : raw;
};

/**
 * Diakritik-insensitive Token-Signatur eines Namens für den Dedup-Vergleich.
 * Wichtig: Kontakte kommen als „Nachname, Vorname", Outlook meist als
 * „Vorname Nachname" — deshalb wird in ein SORTIERTES Token-Set normalisiert,
 * damit die Reihenfolge egal ist. Leere Signatur = nicht dedupbar.
 */
export const nameSignature = (name: string): string =>
    (name || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Diakritik entfernen
        .replace(/[^a-z0-9\s]/g, " ") // Satzzeichen (v. a. Komma) → Leerzeichen
        .split(/\s+/)
        .filter(Boolean)
        .sort()
        .join(" ");

export const GeburtstageCard: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [contacts, setContacts] = useState<DashboardBirthday[]>([]);
    const [outlook, setOutlook] = useState<OutlookBirthday[]>([]);
    const [loadingContacts, setLoadingContacts] = useState(true);
    const [loadingOutlook, setLoadingOutlook] = useState(false);
    // P5: max. 10 zeigen, „Mehr" klappt den Rest in einen Scrollbereich auf.
    const [expanded, setExpanded] = useState(false);

    // Eigene Postfach-Adresse — die Outlook-Geburtstage kommen ausschließlich aus
    // dem persönlichen Graph-Kalender (analog Termine-Karte), unabhängig von der
    // geteilten Overlay-Auswahl des Kalender-Moduls.
    const ownAddress = useMemo(() => (user?.email ?? "").trim().toLowerCase(), [user?.email]);

    // Kontakt-Geburtstage (primäre, schnelle lokale Quelle).
    useEffect(() => {
        let cancelled = false;
        setLoadingContacts(true);
        dashboardService
            .getBirthdaysToday()
            .then((res) => {
                if (!cancelled) setContacts(res);
            })
            .catch(() => {
                if (!cancelled) setContacts([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingContacts(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Outlook-Geburtstage: heutige ganztägige Geburtstags-Events des eigenen
    // Kalenders (Graph). Ohne eigenes Postfach (`user.email` leer) verhält sich
    // die Karte wie bisher — nur Kontakte (Testpunkt 5).
    useEffect(() => {
        if (!ownAddress) {
            setOutlook([]);
            setLoadingOutlook(false);
            return;
        }
        let cancelled = false;
        setLoadingOutlook(true);
        // L1: dasselbe Wochen-Fenster wie die Termine-Karte (Mo–So, top=500), damit
        // sich beide denselben swrCache-Eintrag teilen (1 Kalender-Abruf statt 2);
        // auf „heute" wird clientseitig gefiltert.
        const from = mondayOfWeek(new Date());
        const to = new Date(from);
        to.setDate(to.getDate() + 7);
        calendarService
            .listEventsForMailboxes(
                [{ address: ownAddress, displayName: ownAddress, color: REFINED.aub }],
                from,
                to,
                500,
            )
            .then((list) => {
                if (cancelled) return;
                const births = list
                    .filter((e) => isToday(e.start.dateTime) && istGeburtstagEvent(e.subject, e.isAllDay || false))
                    .map<OutlookBirthday>((e) => ({ id: e.id, name: parseOutlookBirthdayName(e.subject) }))
                    .filter((b) => b.name.length > 0);
                setOutlook(births);
            })
            .catch(() => {
                if (!cancelled) setOutlook([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingOutlook(false);
            });
        return () => {
            cancelled = true;
        };
    }, [ownAddress]);

    // Gemischte Liste: Kontakte zuerst, danach die (dedupten) Outlook-Zeilen.
    // Dedup: ein Kontakt mit gleicher Namens-Signatur unterdrückt den Outlook-
    // Eintrag; zusätzlich werden Outlook-interne Dubletten (gleiche Signatur)
    // zusammengeführt. Zeilen ohne verwertbare Signatur bleiben unangetastet.
    const rows = useMemo<BirthdayRowItem[]>(() => {
        const contactSigs = new Set(contacts.map((c) => nameSignature(c.name)).filter(Boolean));
        const seenOutlook = new Set<string>();
        const out: BirthdayRowItem[] = contacts.map((c) => ({ kind: "contact", contact: c }));
        for (const ob of outlook) {
            const sig = nameSignature(ob.name);
            if (sig) {
                if (contactSigs.has(sig)) continue; // Kontakt gewinnt
                if (seenOutlook.has(sig)) continue; // Outlook-interne Dublette
                seenOutlook.add(sig);
            }
            out.push({ kind: "outlook", outlook: ob });
        }
        return out;
    }, [contacts, outlook]);

    const goContact = (id: number) => navigate(`/contacts/${id}`);

    // Ladeanzeige: solange die Kontakte laden ODER (Kontakte leer, aber Outlook
    // noch unterwegs) — sonst würde kurz „Keine Geburtstage" flackern, obwohl
    // gleich Outlook-Zeilen kommen.
    const showLoading = loadingContacts || (loadingOutlook && rows.length === 0);

    return (
        <DashCard
            title="Geburtstage"
            icon={<Gift20Regular />}
            right={
                !showLoading && rows.length > 0 ? (
                    <span style={{ fontSize: "0.82rem", fontWeight: 700, color: REFINED.aub }}>
                        {rows.length === 1 ? "1 heute" : `${rows.length} heute`}
                    </span>
                ) : undefined
            }
        >
            {showLoading ? (
                <EmptyHint>Lade Geburtstage…</EmptyHint>
            ) : rows.length === 0 ? (
                <EmptyHint>Keine Geburtstage heute.</EmptyHint>
            ) : (
                <>
                    <div
                        style={{
                            padding: "4px 0 2px",
                            ...(expanded ? { maxHeight: 360, overflowY: "auto" } : {}),
                        }}
                    >
                        {(expanded ? rows : rows.slice(0, VISIBLE)).map((r, idx) =>
                            r.kind === "contact" ? (
                                <BirthdayRow
                                    key={`c-${r.contact.id}`}
                                    birthday={r.contact}
                                    border={idx > 0}
                                    onClick={() => goContact(r.contact.id)}
                                />
                            ) : (
                                <OutlookBirthdayRow key={`o-${r.outlook.id}`} outlook={r.outlook} border={idx > 0} />
                            ),
                        )}
                    </div>
                    {!expanded && rows.length > VISIBLE && (
                        <MehrLadenButton rest={rows.length - VISIBLE} label="Mehr" onClick={() => setExpanded(true)} />
                    )}
                </>
            )}
        </DashCard>
    );
};

/* ────────── Eine Kontakt-Geburtstags-Zeile (Avatar + Name + ggf. Alter) ────────── */

const BirthdayRow: React.FC<{ birthday: DashboardBirthday; border: boolean; onClick: () => void }> = ({
    birthday,
    border,
    onClick,
}) => (
    <div
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
            }
        }}
        style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 6px",
            borderTop: border ? `1px solid ${REFINED.line}` : "none",
            cursor: "pointer",
        }}
    >
        <span style={{ flexShrink: 0, display: "inline-flex" }}>
            <InitialsAvatar name={birthday.name} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div
                style={{
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: REFINED.ink,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {birthday.name}
            </div>
            <div style={{ fontSize: "0.83rem", color: REFINED.ink2 }}>
                {birthday.age != null ? `wird heute ${birthday.age}` : "hat heute Geburtstag"}
            </div>
        </div>
        {birthday.statusLabel && (
            <span style={{ flexShrink: 0 }}>
                <StatusChip status={birthday.statusLabel} />
            </span>
        )}
        <span style={{ flexShrink: 0, fontSize: "1.05rem" }} aria-hidden>
            🎂
        </span>
    </div>
);

/* ────────── Eine Outlook-Geburtstags-Zeile (rein informativ, SP2) ──────────
 * Kein Kontakt-Link, kein Alter, nicht klickbar (Entscheidung 5). Optisch wie
 * eine Kontakt-Zeile (Avatar aus Namen), aber mit „Outlook"-Badge statt Status
 * und ohne Cursor/Interaktion. */

const OutlookBirthdayRow: React.FC<{ outlook: OutlookBirthday; border: boolean }> = ({ outlook, border }) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 6px",
            borderTop: border ? `1px solid ${REFINED.line}` : "none",
        }}
    >
        <span style={{ flexShrink: 0, display: "inline-flex" }}>
            <InitialsAvatar name={outlook.name} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div
                style={{
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: REFINED.ink,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {outlook.name}
            </div>
            <div style={{ fontSize: "0.83rem", color: REFINED.ink2 }}>hat heute Geburtstag</div>
        </div>
        <span style={{ flexShrink: 0 }}>
            <OutlookBadge />
        </span>
        <span style={{ flexShrink: 0, fontSize: "1.05rem" }} aria-hidden>
            🎂
        </span>
    </div>
);

export default GeburtstageCard;
